import React, { useState } from 'react';
import { ErrorFallback } from '../components/ErrorFallback';
import { parseErrorToString } from './parseErrorToString';

type ErrorBoundaryProps = {
	children: React.ReactNode;
};

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
	const [hasError, setHasError] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const logError = (errorToLog: Error, componentStack?: string | null): void => {
		console.error(errorToLog);
		if (componentStack) {
			console.error(componentStack);
		}
	};

	const componentDidCatch = (componentDidCatchError: Error, info: { componentStack: string }): void => {
		logError(componentDidCatchError, info.componentStack);
		setError(componentDidCatchError);
		setHasError(true);
	};

	if (hasError && error) {
		const readableError = parseErrorToString(error);
		return <ErrorFallback error={readableError} />;
	}

	return <ErrorBoundaryWrapper onError={componentDidCatch}>{children}</ErrorBoundaryWrapper>;
};

type ErrorBoundaryWrapperProps = {
	children: React.ReactNode;
	onError: (error: Error, info: { componentStack: string }) => void;
};

class ErrorBoundaryWrapper extends React.Component<ErrorBoundaryWrapperProps, { hasError: boolean }> {
	constructor(props: ErrorBoundaryWrapperProps) {
		super(props);
		this.state = { hasError: false };
	}

	componentDidCatch(error: unknown, info: { componentStack: string }) {
		// Normalize any type of thrown value into an Error
		const normalizedError = error instanceof Error ? error : new Error(String(error));
		this.props.onError(normalizedError, info);
		this.setState({ hasError: true });
	}

	render() {
		// Optionally: add inline fallback here too
		return this.props.children;
	}
}

export default ErrorBoundary;
